import { supabase } from './supabase'

/**
 * Master Admin Storage Management Service
 * Allows master admins to set storage limits per tier and per organization
 */

/**
 * Get all storage limits (tier defaults and organization overrides)
 */
export const getAllStorageLimits = async () => {
  try {
    const { data, error } = await supabase
      .from('storage_limits')
      .select(`
        *,
        subscription_tiers(name, id),
        organizations(name, id)
      `)
      .order('created_at', { ascending: false })

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting storage limits:', error)
    throw error
  }
}

/**
 * Get storage limits for a specific tier
 */
export const getTierStorageLimits = async (tierId) => {
  try {
    const { data, error } = await supabase
      .from('storage_limits')
      .select('*')
      .eq('subscription_tier_id', tierId)
      .is('organization_id', null)
      .single()

    if (error && error.code !== 'PGRST116') {
      throw error
    }

    return { success: true, data: data || null }
  } catch (error) {
    console.error('Error getting tier storage limits:', error)
    throw error
  }
}

/**
 * Set storage limits for a subscription tier (default for all orgs on that tier)
 */
export const setTierStorageLimits = async (tierId, limits) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    // Check if limit already exists
    const { data: existing } = await supabase
      .from('storage_limits')
      .select('id')
      .eq('subscription_tier_id', tierId)
      .is('organization_id', null)
      .single()

    const limitData = {
      subscription_tier_id: tierId,
      organization_id: null,
      max_storage_bytes: limits.maxStorageBytes,
      max_file_size_bytes: limits.maxFileSizeBytes,
      max_files_per_organization: limits.maxFilesPerOrganization || null,
      is_custom: false,
      updated_by: user?.id,
      updated_at: new Date().toISOString(),
    }

    let result
    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('storage_limits')
        .update(limitData)
        .eq('id', existing.id)
        .select()
        .single()

      if (error) throw error
      result = data
    } else {
      // Create new
      limitData.created_by = user?.id
      const { data, error } = await supabase
        .from('storage_limits')
        .insert(limitData)
        .select()
        .single()

      if (error) throw error
      result = data
    }

    return { success: true, data: result }
  } catch (error) {
    console.error('Error setting tier storage limits:', error)
    throw error
  }
}

/**
 * Set custom storage limits for a specific organization (overrides tier default)
 */
export const setOrganizationStorageLimits = async (organizationId, limits) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    // Check if custom limit already exists
    const { data: existing } = await supabase
      .from('storage_limits')
      .select('id')
      .eq('organization_id', organizationId)
      .single()

    const limitData = {
      subscription_tier_id: null,
      organization_id: organizationId,
      max_storage_bytes: limits.maxStorageBytes,
      max_file_size_bytes: limits.maxFileSizeBytes,
      max_files_per_organization: limits.maxFilesPerOrganization || null,
      is_custom: true,
      updated_by: user?.id,
      updated_at: new Date().toISOString(),
    }

    let result
    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('storage_limits')
        .update(limitData)
        .eq('id', existing.id)
        .select()
        .single()

      if (error) throw error
      result = data
    } else {
      // Create new
      limitData.created_by = user?.id
      const { data, error } = await supabase
        .from('storage_limits')
        .insert(limitData)
        .select()
        .single()

      if (error) throw error
      result = data
    }

    return { success: true, data: result }
  } catch (error) {
    console.error('Error setting organization storage limits:', error)
    throw error
  }
}

/**
 * Remove custom storage limits for an organization (revert to tier default)
 */
export const removeOrganizationStorageLimits = async (organizationId) => {
  try {
    const { error } = await supabase
      .from('storage_limits')
      .delete()
      .eq('organization_id', organizationId)
      .eq('is_custom', true)

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error removing organization storage limits:', error)
    throw error
  }
}

/**
 * Get all organizations with their storage usage
 */
export const getAllOrganizationsStorageUsage = async () => {
  try {
    const { data, error } = await supabase
      .from('storage_usage')
      .select(`
        *,
        organizations(id, name)
      `)
      .order('total_bytes_used', { ascending: false })

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting all organizations storage usage:', error)
    throw error
  }
}

